import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Api } from 'lib/api.mjs';
import { DEFAULT_WIN_ROAS, DEFAULT_WIN_SPEND } from 'lib/winnerRules.mjs';
import { useAuth } from './AuthContext.jsx';
const defaults = { roas: DEFAULT_WIN_ROAS, spend: DEFAULT_WIN_SPEND };
const Context = createContext({ winRoas: defaults.roas, winSpend: defaults.spend, loaded: false });
export function WinnerRulesProvider({ children }) {
  const { token } = useAuth();
  const [config, setConfig] = useState(defaults);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');
  const sequence = useRef(0);
  const accept = useCallback(next => {
    sequence.current++;
    setConfig(next); setLoaded(true); setError('');
  }, []);
  // The dashboard also carries the current organization rule. If the separate
  // settings read fails, use that rule for displays while keeping editing
  // disabled until settings are loaded. Never replace a confirmed save/read.
  const seedFromDashboard = useCallback(analytics => {
    if (loaded) return;
    const roas = Number(analytics?.winThresholdRoas);
    const spend = Number(analytics?.winThresholdSpend);
    if (Number.isFinite(roas) && roas > 0 && Number.isFinite(spend) && spend > 0) {
      setConfig(previous => previous.roas === roas && previous.spend === spend ? previous : { roas, spend });
    }
  }, [loaded]);
  const refresh = useCallback(async signal => {
    const request = ++sequence.current;
    const data = await Api.get('/winner-config', { signal });
    if (signal?.aborted || request !== sequence.current) return;
    if (data?.config) accept(data.config);
    else setError(data?.error || 'Could not load the winning ROAS setting.');
  }, [accept]);
  useEffect(() => {
    setLoaded(false); setConfig(defaults);
    if (!token) return;
    const ctrl = new AbortController();
    refresh(ctrl.signal);
    const onFocus = () => refresh(ctrl.signal);
    const timer = setInterval(() => { if (document.visibilityState === 'visible') refresh(ctrl.signal); }, 30000);
    window.addEventListener('focus', onFocus);
    return () => { ctrl.abort(); clearInterval(timer); window.removeEventListener('focus', onFocus); sequence.current++; };
  }, [token, refresh]);
  const value = useMemo(() => ({ winRoas: config.roas, winSpend: config.spend, loaded, error, refresh, accept, seedFromDashboard }), [config, loaded, error, refresh, accept, seedFromDashboard]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}
export const useWinnerRules = () => useContext(Context);
